import { getFolders } from '../collectionFolders';
import { normalizeRequestBodyMode } from '../requestBodyMode';
import { getFormDataFieldsFromRequest } from '../multipart';

function serializeUrlencoded(fields) {
  const params = new URLSearchParams();
  for (const f of fields) {
    if (f.key && f.enabled !== false) params.append(f.key, f.value ?? '');
  }
  return params.toString();
}

/**
 * Export a sidebar collection to the Insomnia v4 export shape this app imports
 * (`parseInsomnia.js`) — PascalCase `_type` resources under a single Workspace.
 * Folders become RequestGroups; requests become Requests parented to their folder
 * (or the workspace when at root).
 */

function mapAuth(auth, warnings) {
  if (!auth || auth.type === 'noauth' || auth.type === 'inherit') return undefined;
  switch (auth.type) {
    case 'bearer':
      return { type: 'bearer', token: auth.bearer ?? '', disabled: false };
    case 'basic':
      return {
        type: 'basic',
        username: auth.basic?.username ?? '',
        password: auth.basic?.password ?? '',
        disabled: false,
      };
    default:
      warnings?.push(`Insomnia export: auth type "${auth.type}" is not supported and was dropped.`);
      return undefined;
  }
}

function mapBody(request, warnings) {
  const { bodyType, rawBodyLanguage } = normalizeRequestBodyMode(request);

  if (bodyType === 'none') return {};

  if (bodyType === 'urlencoded') {
    const fields = getFormDataFieldsFromRequest(request).filter((f) => f.key);
    // `text` is what this app's importer reads; `params` keeps real Insomnia happy.
    return {
      mimeType: 'application/x-www-form-urlencoded',
      text: serializeUrlencoded(fields),
      params: fields.map((f) => ({ name: f.key, value: f.value ?? '', disabled: f.enabled === false })),
    };
  }

  if (bodyType === 'form-data') {
    warnings?.push('Insomnia export: multipart/form-data fields cannot round-trip through this app\'s Insomnia importer (imported as raw).');
    const params = getFormDataFieldsFromRequest(request)
      .filter((f) => f.key)
      .map((f) =>
        f.type === 'file'
          ? { name: f.key, type: 'file', fileName: f.filePath ?? '', disabled: f.enabled === false }
          : { name: f.key, value: f.value ?? '', disabled: f.enabled === false }
      );
    return { mimeType: 'multipart/form-data', text: '', params };
  }

  if (bodyType === 'graphql') {
    // Our graphql body is a JSON string {query,variables}; Insomnia stores the
    // same JSON under the graphql mime. Re-imports as raw text (acceptable).
    return { mimeType: 'application/graphql', text: request.body || '' };
  }

  const mimeType =
    rawBodyLanguage === 'json'
      ? 'application/json'
      : rawBodyLanguage === 'xml'
        ? 'application/xml'
        : 'text/plain';
  return { mimeType, text: request.body || '' };
}

function mapRequest(request, id, parentId, warnings) {
  const headers = (request.headers || [])
    .filter((h) => h.key)
    .map((h) => ({ name: h.key, value: h.value ?? '', disabled: h.enabled === false }));
  const parameters = (request.params || [])
    .filter((p) => p.key)
    .map((p) => ({ name: p.key, value: p.value ?? '', disabled: p.enabled === false }));

  const resource = {
    _id: id,
    _type: 'Request',
    parentId,
    name: request.name || 'Request',
    description: request.description || '',
    method: (request.method || 'GET').toUpperCase(),
    url: request.url || '',
    headers,
    parameters,
    body: mapBody(request, warnings),
    authentication: mapAuth(request.auth, warnings) || {},
  };

  const pre = request.preRequestScript ?? request.pre_request_script ?? '';
  const test = request.testScript ?? request.test_script ?? '';
  if (pre.trim()) resource.preRequestScript = pre;
  if (test.trim()) resource.afterResponseScript = test;
  return resource;
}

/**
 * @param {object} collection Sidebar collection
 * @param {{ push: (msg: string) => void }} warnings
 */
export function toInsomniaExport(collection, warnings) {
  const workspaceId = 'wrk_apiboo';
  const resources = [
    {
      _id: workspaceId,
      _type: 'Workspace',
      parentId: null,
      name: collection.name || 'Collection',
      description: collection.description || '',
    },
  ];

  const folders = getFolders(collection);
  const folderResourceId = (fid) => (fid == null ? workspaceId : `fld_${fid}`);
  for (const folder of folders) {
    resources.push({
      _id: folderResourceId(folder.id),
      _type: 'RequestGroup',
      parentId: folder.parent_id == null ? workspaceId : folderResourceId(folder.parent_id),
      name: folder.name || 'Folder',
      description: folder.description || '',
    });
  }

  (collection.requests || []).forEach((req, idx) => {
    const parentId = folderResourceId(req.folder_id ?? req.settings?.folder_id ?? null);
    resources.push(mapRequest(req, `req_${idx}`, parentId, warnings));
  });

  return {
    _type: 'export',
    __export_format: 4,
    __export_date: new Date().toISOString(),
    __export_source: 'apiboo',
    resources,
  };
}
